import { useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Check } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";

export default function QuoteModal({ product, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", quantity: "", message: "" });
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/quotes", { ...form, product_id: product?.id || null, product_name: product?.name || "" });
      setState("done");
    } catch { setState("error"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} className="fixed inset-0 bg-inverse/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4" data-testid="quote-modal">
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()} className="bg-[color:var(--bg)] w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
          <h3 className="font-display font-extrabold text-xl">{t("quote.title")}</h3>
          <button onClick={onClose} data-testid="quote-close"><X className="w-5 h-5" /></button>
        </div>

        {state === "done" ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 bg-gold text-inverse flex items-center justify-center mx-auto mb-5"><Check className="w-7 h-7" /></div>
            <p className="font-display font-medium text-lg" data-testid="quote-success">{t("quote.success")}</p>
            <button onClick={onClose} className="mt-6 bg-blue text-[color:var(--bg)] px-6 py-3 hover:bg-gold hover:text-inverse transition-colors">OK</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            {product && <p className="text-sm text-gold font-mono mb-2">{product.name}</p>}
            <p className="text-sm text-ink/55 mb-2">{t("quote.subtitle")}</p>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("quote.name")} data-testid="quote-name" className="qinp" />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder={t("quote.company")} className="qinp" />
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("quote.phone")} className="qinp" />
            </div>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={t("quote.email")} data-testid="quote-email" className="qinp" />
            <input value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder={t("quote.qty")} className="qinp" />
            <textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={t("quote.message")} rows={3} className="qinp resize-none" />
            {state === "error" && <p className="text-red-600 text-sm">{t("quote.error")}</p>}
            <button type="submit" disabled={state === "sending"} data-testid="quote-submit"
              className="w-full bg-blue text-[color:var(--bg)] py-3.5 flex items-center justify-center gap-2 hover:bg-gold hover:text-inverse transition-colors font-medium disabled:opacity-60">
              <Send className="w-4 h-4" /> {state === "sending" ? t("quote.sending") : t("quote.send")}
            </button>
          </form>
        )}
        <style>{`.qinp{width:100%;border:1px solid rgba(10,18,26,.15);background:var(--bg);padding:.7rem .85rem;outline:none;font-size:.9rem}.qinp:focus{border-color:var(--gold)}`}</style>
      </motion.div>
    </motion.div>
  );
}
